import type { AdminNotificationStackItem } from '@/lib/admin-notification-stack.types';
import type { AdminNotification } from '@/lib/notifications';

export type EntityAlertNotificationFields = {
  key: string;
  severity: string;
  sopId?: number | null;
  siteId?: number | null;
  entityType?: string | null;
  entityId?: number | null;
};

export type EntityAlertScope =
  | { kind: 'sop'; sopId: number }
  | { kind: 'entity'; entityType: string; entityId: number }
  | { kind: 'site'; siteId: number }
  | { kind: 'single'; notificationKey: string };

function normalizePositiveInt(value: number | null | undefined): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function isCriticalNotificationSeverity(severity: string): boolean {
  return severity === 'error' || severity === 'critical';
}

function resolveSopIdFromFields(notification: EntityAlertNotificationFields): number | null {
  const fromColumn = normalizePositiveInt(notification.sopId);
  if (fromColumn != null) return fromColumn;
  if (notification.entityType?.trim() === 'sop') {
    return normalizePositiveInt(notification.entityId);
  }
  return null;
}

export function entityAlertFieldsFromApi(row: AdminNotification): EntityAlertNotificationFields {
  return {
    key: row.key,
    severity: row.severity,
    sopId: row.sopId ?? (row.entityType === 'sop' ? row.entityId : null),
    siteId: row.siteId,
    entityType: row.entityType,
    entityId: row.entityId,
  };
}

export function entityAlertFieldsFromStackItem(item: AdminNotificationStackItem): EntityAlertNotificationFields {
  return {
    key: item.id,
    severity: item.severity === 'critical' ? 'error' : item.severity,
    sopId: item.sopId ?? (item.entityType === 'sop' ? item.entityId : null),
    siteId: item.siteId,
    entityType: item.entityType,
    entityId: item.entityId,
  };
}

export function resolveEntityAlertScope(notification: EntityAlertNotificationFields): EntityAlertScope {
  const sopId = resolveSopIdFromFields(notification);
  if (sopId != null) return { kind: 'sop', sopId };

  const entityType = notification.entityType?.trim();
  const entityId = normalizePositiveInt(notification.entityId);
  if (entityType && entityId != null) {
    return { kind: 'entity', entityType, entityId };
  }

  const siteId = normalizePositiveInt(notification.siteId);
  if (siteId != null) return { kind: 'site', siteId };

  return { kind: 'single', notificationKey: notification.key };
}

export function notificationMatchesEntityScope(
  notification: EntityAlertNotificationFields,
  scope: EntityAlertScope,
): boolean {
  switch (scope.kind) {
    case 'sop':
      return resolveSopIdFromFields(notification) === scope.sopId;
    case 'entity':
      return (
        notification.entityType?.trim() === scope.entityType
        && normalizePositiveInt(notification.entityId) === scope.entityId
      );
    case 'site':
      return normalizePositiveInt(notification.siteId) === scope.siteId;
    case 'single':
      return notification.key === scope.notificationKey;
    default:
      return false;
  }
}

export function filterCriticalEntityAlerts<T extends EntityAlertNotificationFields>(
  notifications: T[],
  scope: EntityAlertScope,
): T[] {
  return notifications.filter(
    (notification) =>
      isCriticalNotificationSeverity(notification.severity)
      && notificationMatchesEntityScope(notification, scope),
  );
}

const CANONICAL_SOP_STRUCTURAL_TYPES = new Set([
  'sop_incomplete_flow',
  'sop_no_end_step',
  'sop_no_submit_terminal',
  'step_missing_contact_reference',
  'step_scoped_asset_ref_invalid',
]);

export function isCanonicalSopStructuralNotificationType(type: string): boolean {
  return CANONICAL_SOP_STRUCTURAL_TYPES.has(type);
}

export type SopFlowEntityAlertRow = EntityAlertNotificationFields & {
  type: string;
};

export type DedupedEntityAlerts<T extends SopFlowEntityAlertRow> = {
  alerts: T[];
  suppressedScenarioCount: number;
};

/** Hide readiness duplicates when a canonical SOP structural alert covers the same sopId. */
export function dedupeRedundantScenarioSopFlowAlerts<T extends SopFlowEntityAlertRow>(
  rows: T[],
): DedupedEntityAlerts<T> {
  const sopIdsWithCanonical = new Set<number>();
  for (const row of rows) {
    if (!CANONICAL_SOP_STRUCTURAL_TYPES.has(row.type)) continue;
    const sopId = resolveSopIdFromFields(row);
    if (sopId != null) sopIdsWithCanonical.add(sopId);
  }
  if (sopIdsWithCanonical.size === 0) {
    return { alerts: rows, suppressedScenarioCount: 0 };
  }

  let suppressedScenarioCount = 0;
  const alerts = rows.filter((row) => {
    if (row.type !== 'scenario_invalid_sop_flow') return true;
    const sopId = resolveSopIdFromFields(row);
    if (sopId != null && sopIdsWithCanonical.has(sopId)) {
      suppressedScenarioCount += 1;
      return false;
    }
    return true;
  });

  return { alerts, suppressedScenarioCount };
}
